import contentfulGql from 'js/lib/contentfulGql';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import { getGrowthCmsGraphqlContext } from 'bundles/cms/utils/CmsUtils';
import type {
  CourseraPlusCtaPart,
  CourseraPlusLandingPage,
} from 'bundles/coursera-plus-landing-page/__generated__/baseContentfulTypes';
import type {
  LandingPageQueryQuery,
  LandingPageQueryQueryVariables,
} from 'bundles/coursera-plus-landing-page/__generated__/contentfulTypes';

export type CourseraPlusCtaPartPicked = Pick<
  CourseraPlusCtaPart,
  | 'entryTitle'
  | 'freeform'
  | 'pretranslated'
  | 'size'
  | 'line'
  | 'product'
  | 'presentation'
  | 'hideInLocation'
  | 'conditionOfShowing'
  | 'displayAsBold'
  | 'freeformRich'
  | 'topMargin'
  | 'bottomMargin'
  | 'area'
>;

export const landingPageQuery = contentfulGql`#graphql
  query LandingPageQuery($slug: String!, $preview: Boolean!, $locale: String) {
    courseraPlusLandingPageCollection(preview: $preview, where: { slug: $slug }, locale: $locale, limit: 1) {
      items {
        title
        slug
        primaryCourseraPlusProduct
        secondaryCourseraPlusProduct
        heroDesignChoice
        heroTextBig
        heroImage
          { url } 
        heroTextMedium2 
          { json }
        heroTextSmall2 
          { json }
        footerText2
          { json }
        showFooterTextOnMobile
        stickyBannerText
        labelForPrimaryCheckout
        promotionCode
        pageExpiry
        termsAndConditionsPageLink
        countriesToInclude
        countriesToExclude
        cutPartnerLogos
        cutValueProps
        cutRecommendations
        cutCourseSearch
        cutLearnerOutcome
        cutPriceComparison
        cutFaq
        cutFooter
        modalForUserWhoOwnsCourseraPlus
        modalForUserWhoUsedUpTheirFreeTrial
        freeTrial
        landingPageType
        activatePurchaseTracker
        customProductCollectionAtTheTop
        customProductCollectionAtBottom
        explanationForFaq
          {json}
        skipQuestionInFaqAboutFreeTrial
        heroCtaPartsCollection {
          items {
            hideInLocation
            entryTitle
            product
            freeform
            pretranslated
            size
            line
            presentation
            conditionOfShowing
            displayAsBold
            freeformRich {json}
            topMargin
            bottomMargin, 
            area
          }
      }
      }
  }
}`;

export type PropsForLandingPageQuery = {
  slug: string;
  preview: boolean;
  locale: string;
};

export type LandingPagePicked = Pick<
  CourseraPlusLandingPage,
  | 'title'
  | 'slug'
  | 'primaryCourseraPlusProduct'
  | 'secondaryCourseraPlusProduct'
  | 'promotionCode'
  | 'pageExpiry'
  | 'termsAndConditionsPageLink'
  | 'heroDesignChoice'
  | 'heroImage'
  | 'heroTextBig'
  | 'heroTextMedium2'
  | 'heroTextSmall2'
  | 'footerText2'
  | 'stickyBannerText'
  | 'showFooterTextOnMobile'
  | 'labelForPrimaryCheckout'
  | 'heroCtaPartsCollection'
  | 'cutPartnerLogos'
  | 'cutValueProps'
  | 'cutRecommendations'
  | 'cutCourseSearch'
  | 'cutLearnerOutcome'
  | 'cutPriceComparison'
  | 'cutFaq'
  | 'cutFooter'
  | 'modalForUserWhoOwnsCourseraPlus'
  | 'modalForUserWhoUsedUpTheirFreeTrial'
  | 'explanationForFaq'
  | 'skipQuestionInFaqAboutFreeTrial'
  | 'freeTrial'
  | 'landingPageType'
  | 'activatePurchaseTracker'
  | 'customProductCollectionAtTheTop'
  | 'customProductCollectionAtBottom'
>;

export type PropsFromLandingPage = {
  landingPage: CourseraPlusLandingPage | null | undefined;
};

export const withLandingPageData = () =>
  waitForGraphQL<PropsForLandingPageQuery, LandingPageQueryQuery, LandingPageQueryQueryVariables, PropsFromLandingPage>(
    landingPageQuery,
    {
      options: ({ slug, preview, locale }) => ({
        variables: { slug, preview, locale },
        context: getGrowthCmsGraphqlContext(preview),
      }),
      props: ({ data }) => {
        const landingPage = data?.courseraPlusLandingPageCollection?.items[0];
        return { landingPage } as PropsFromLandingPage;
      },
    }
  );
